import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "./api";

const AuthContext = createContext({
    user: null,
    loading: true,
    isPremium: false,
    refresh: async () => {},
    logout: async () => {},
});

const PREMIUM_LOCAL_KEY = "moodcard.premium.local";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localPremium, setLocalPremium] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Skip /me check if returning from auth callback — AuthCallback handles it.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        // iter-101 — clear the stale localPremium flag on mount. The flag
        // wrongly persisted "paid" state across users + sessions.
        try {
            localStorage.removeItem(PREMIUM_LOCAL_KEY);
            setLocalPremium(false);
        } catch { /* ignore */ }
        refresh();
    }, [refresh]);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch { /* ignore */ }
        setUser(null);
    }, []);

    const setLocalPremiumFlag = useCallback((v) => {
        try {
            if (v) localStorage.setItem(PREMIUM_LOCAL_KEY, "1");
            else localStorage.removeItem(PREMIUM_LOCAL_KEY);
        } catch { /* ignore */ }
        setLocalPremium(!!v);
    }, []);

    // iter-101 — SECURITY/INTEGRITY FIX
    // Backend gates Style Packs / Motion / Keeper Studio on tier ∈ {premium, creator}.
    // Plus tier ($7.99) gets more daily prompts + cleaner exports, NOT locked packs —
    // so isPremium must mirror the backend gate, not just "paid anything."
    //
    // The legacy `localPremium` localStorage flag (previously set by promo
    // flows / dev) was wrongly granting paid-feature access to non-payers.
    // It's now IGNORED. Only server-confirmed `user.tier` counts.
    // Stale flag is cleared on mount.
    // iter-104 — Plus and Keeper collapsed into ONE paid tier.
    // Any paid tier now unlocks everything (style packs, animated exports,
    // brand colors, motion). Frontend gate matches backend.
    const paidTier = (user && (user.tier || user.premium_tier) || "").toLowerCase();
    const tierIsPremium = paidTier === "plus" || paidTier === "premium" || paidTier === "creator";
    // Legacy back-compat: very old user docs only have boolean `premium` with no tier.
    const legacyPremium = !!(user && user.premium && !paidTier);
    const isPremium = tierIsPremium || legacyPremium;
    const isAdmin = !!(user && user.is_admin);

    return (
        <AuthContext.Provider value={{ user, loading, isPremium, isAdmin, refresh, logout, setLocalPremiumFlag }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
